import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import {
  Calculator, CalendarDays, TrendingUp, Plus, Trash2, Save,
  Pencil, RefreshCw, Info, ChevronDown, BarChart2, Target,
  DollarSign, Users, Phone, Eye
} from 'lucide-react';
import { format } from 'date-fns';

const CHART_COLORS = ['#06B6D4','#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444','#EC4899','#14B8A6','#F97316','#84CC16'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—';
const fmtPct = (v) => v != null ? Number(v).toFixed(1) + '%' : '—';
const fmtNum = (v) => v != null && v !== '' ? Number(v).toLocaleString() : '—';
const safe = (v) => v === '' || v == null ? '' : v;

// ─── Shared KPI Summary Card ──────────────────────────────────────────────────
const KpiSummaryCard = ({ label, value, color, icon: Icon, sub }) => (
  <Card className="bg-white shadow-sm" style={{ borderLeft: `3px solid ${color}` }}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-1">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        {Icon && <Icon size={14} style={{ color }} />}
      </div>
      <p className="text-xl font-semibold tabular-nums font-heading text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

// ─── Inline-editable number cell ─────────────────────────────────────────────
// ─── Inline-editable number cell ──────────────────────────────────────────────
const NumCell = ({ value, onChange, placeholder, prefix, readOnly, highlight }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const inputRef = useRef();
  const start = () => { if (readOnly) return; setLocal(value === '' || value == null ? '' : String(value)); setEditing(true); };
  const commit = () => { setEditing(false); const n = parseFloat(local); onChange(isNaN(n) ? null : n); };
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') commit(); if (e.key === 'Escape') setEditing(false); }}
        className="w-full border border-cyan-400 rounded px-1.5 py-0.5 text-xs text-slate-900 bg-cyan-50 outline-none"
        placeholder={placeholder}
        type="number"
        min="0"
      />
    );
  }
  const display = value === '' || value == null ? '' : (prefix ? prefix + Number(value).toLocaleString() : Number(value).toLocaleString());
  return (
    <div
      onClick={start}
      className={`min-w-[60px] px-1.5 py-0.5 rounded text-xs cursor-pointer select-none ${
        readOnly
          ? 'text-slate-400 cursor-default'
          : display
            ? `font-medium text-slate-800 hover:bg-cyan-50 border border-transparent hover:border-cyan-200 ${ highlight ? 'text-cyan-700' : ''}`
            : 'text-slate-300 hover:bg-slate-50 border border-dashed border-slate-200'
      }`}
    >
      {display || (readOnly ? '—' : placeholder || 'click')}
    </div>
  );
};

// ─── Calculated KPI cell ─────────────────────────────────────────────────────
// ─── Calculated (read-only) cell ──────────────────────────────────────────────
const CalcCell = ({ value, fmt, good, bad }) => {
  if (value == null || value === undefined) return <span className="text-slate-300 text-xs">—</span>;
  const num = Number(value);
  let color = 'text-slate-700';
  if (good != null && bad != null) {
    if (num >= good) color = 'text-green-700';
    else if (num <= bad) color = 'text-red-500';
    else color = 'text-amber-600';
  }
  return <span className={`text-xs font-semibold tabular-nums ${color}`}>{fmt(value)}</span>;
};

// ─── Marketing Table ──────────────────────────────────────────────────────────
// ─── Marketing Data Table ─────────────────────────────────────────────────────
const MarketingTable = ({ rows, totals, onRowChange, onRemoveRow, readOnly, showSaleData, roasMode }) => {
  // roasMode: 'roas' = ad spend only, 'overall' = ad spend + management fees
  const cplKey = roasMode === 'overall' ? 'overall_cpl' : 'cpl';
  const bookingKey = roasMode === 'overall' ? 'overall_cost_per_booking' : 'cost_per_booking';
  const archKey = roasMode === 'overall' ? 'overall_cost_per_arch' : 'cost_per_arch';
  const roiKey = roasMode === 'overall' ? 'overall_roi' : 'roas';
  const roiLabel = roasMode === 'overall' ? 'Overall ROI%' : 'ROAS';

  const cols = [
    { key: 'source_name', label: 'Source', width: 'min-w-[130px]', fixed: true },
    { key: 'spend', label: 'Ad Spend ($)', width: 'min-w-[90px]', input: true, prefix: '$' },
    { key: 'management_fees', label: 'Mgmt Fees ($)', width: 'min-w-[100px]', input: true, prefix: '$' },
    { key: 'leads', label: 'Total Leads', width: 'min-w-[80px]', input: true },
    { key: 'booked', label: 'Booked', width: 'min-w-[70px]', input: true },
    { key: 'shows', label: 'Shows', width: 'min-w-[70px]', input: true },
    { key: cplKey, label: 'CPL', width: 'min-w-[70px]', calc: true, fmt: fmt$, good: null, bad: null },
    { key: bookingKey, label: '$/Booking', width: 'min-w-[80px]', calc: true, fmt: fmt$ },
    { key: 'booking_rate', label: 'Book%', width: 'min-w-[65px]', calc: true, fmt: fmtPct, good: 50, bad: 25 },
    { key: 'show_rate', label: 'Show%', width: 'min-w-[65px]', calc: true, fmt: fmtPct, good: 60, bad: 35 },
    { key: 'cost_per_show', label: '$/Show', width: 'min-w-[75px]', calc: true, fmt: fmt$ },
    ...(showSaleData ? [
      { key: 'arches_sold', label: 'Arches', width: 'min-w-[65px]', input: !readOnly, calc: readOnly, fmt: fmtNum },
      { key: 'patients_sold', label: 'Patients', width: 'min-w-[65px]', input: !readOnly, calc: readOnly, fmt: fmtNum },
      { key: archKey, label: '$/Arch', width: 'min-w-[75px]', calc: true, fmt: fmt$ },
      { key: 'cost_per_patient', label: '$/Patient', width: 'min-w-[80px]', calc: true, fmt: fmt$ },
      { key: 'revenue', label: 'Revenue', width: 'min-w-[85px]', calc: true, fmt: fmt$ },
      { key: roiKey, label: roiLabel, width: 'min-w-[75px]', calc: true, fmt: roasMode === 'overall' ? fmtPct : (v => v != null ? v + 'x' : '—') },
    ] : [
      { key: 'arches_sold', label: 'Arches ✏', width: 'min-w-[70px]', input: true, fmt: fmtNum },
      { key: 'patients_sold', label: 'Patients ✏', width: 'min-w-[70px]', input: true, fmt: fmtNum },
      { key: archKey, label: '$/Arch', width: 'min-w-[75px]', calc: true, fmt: fmt$ },
      { key: 'cost_per_patient', label: '$/Patient', width: 'min-w-[80px]', calc: true, fmt: fmt$ },
    ])
  ];

  const isOther = (r) => r.source_name === 'Other (calculated)';
  const isTotal = (r) => r.source_name === 'TOTAL';

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {cols.map(c => (
              <th key={c.key} className={`${c.width} px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap`}>{c.label}</th>
            ))}
            {!readOnly && <th className="w-8 px-1"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isTot = isTotal(row);
            const isOth = isOther(row);
            return (
              <tr
                key={row.source_name + i}
                className={`border-b border-slate-100 ${
                  isTot ? 'bg-slate-900 text-white font-semibold sticky bottom-0' :
                  isOth ? 'bg-amber-50/60' : 'hover:bg-slate-50'
                }`}
              >
                {cols.map(c => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap">
                    {c.key === 'source_name' ? (
                      <span className={`font-medium ${ isTot ? 'text-cyan-400' : isOth ? 'text-amber-700' : 'text-slate-800' }`}>
                        {row.source_name}
                      </span>
                    ) : c.calc || (isTot && !c.input) || isOth ? (
                      <CalcCell value={row[c.key]} fmt={c.fmt || fmtNum} good={c.good} bad={c.bad} />
                    ) : c.input && !isTot && !isOth ? (
                      <NumCell
                        value={row[c.key]}
                        onChange={v => onRowChange?.(i, c.key, v)}
                        prefix={c.prefix}
                        readOnly={readOnly}
                      />
                    ) : (
                      <CalcCell value={row[c.key]} fmt={c.fmt || fmtNum} good={c.good} bad={c.bad} />
                    )}
                  </td>
                ))}
                {!readOnly && (
                  <td className="px-1 py-2">
                    {!isTot && !isOth && (
                      <button onClick={() => onRemoveRow?.(i)} className="text-slate-300 hover:text-red-400 p-0.5">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Sandbox Calculator ───────────────────────────────────────────────────────
// ─── Sandbox Calculator (play mode, no persistence) ──────────────────────────
const SandboxCalculator = ({ dropdowns, roasMode }) => {
  const DEFAULT_SOURCES = ['Facebook Ads', 'Google Ads', 'TV'];
  const [sources, setSources] = useState(
    DEFAULT_SOURCES.map(s => ({ source_name: s, spend: null, leads: null, booked: null, shows: null, arches_sold: null, patients_sold: null }))
  );
  const [totals, setTotals] = useState({ spend: null, leads: null, booked: null, shows: null });
  const [result, setResult] = useState(null);
  const [addSource, setAddSource] = useState('');

  const allSources = dropdowns?.referral_sources || [];
  const usedSources = sources.map(s => s.source_name);
  const availableSources = allSources.filter(s => !usedSources.includes(s));

  const handleRowChange = (idx, key, val) => {
    setSources(prev => { const n = [...prev]; n[idx] = { ...n[idx], [key]: val }; return n; });
  };
  const handleRemove = (idx) => setSources(prev => prev.filter((_, i) => i !== idx));
  const handleAddSource = () => {
    if (!addSource) return;
    setSources(prev => [...prev, { source_name: addSource, spend: null, leads: null, booked: null, shows: null, arches_sold: null, patients_sold: null }]);
    setAddSource('');
  };

  // Auto-calculate on any change
  useEffect(() => {
    const payload = {
      month: '0000-00',
      sources: sources.map(s => ({ ...s })),
      total_spend: totals.spend || null,
      total_leads: totals.leads || null,
      total_booked: totals.booked || null,
      total_shows: totals.shows || null,
    };
    axios.post(`${API}/marketing/sandbox/calculate`, payload)
      .then(r => setResult(r.data))
      .catch(e => console.warn("[Marketing]", e?.message || e));
  }, [sources, totals]);

  const rows = result ? result.rows : sources.map(s => ({ ...s, cpl: null, booking_rate: null, show_rate: null, cost_per_show: null, cost_per_arch: null, cost_per_patient: null }));
  const tot = result?.totals;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <Calculator size={16} className="text-amber-600 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          <strong>Sandbox Mode</strong> — All inputs, real-time calculations. Nothing is saved. Use this to model scenarios before committing to a monthly entry.
        </p>
      </div>

      {/* Grand Total Overrides */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <span>Grand Totals</span>
            <span className="text-xs font-normal text-slate-400">(optional — unlocks "Other" auto-calculation)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[['spend','Total Spend ($)','$'],['leads','Total Leads',''],['booked','Total Booked',''],['shows','Total Shows','']].map(([k,lbl,pfx]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs text-slate-600">{lbl}</Label>
                <Input
                  type="number" min="0"
                  value={safe(totals[k])}
                  onChange={e => setTotals(p => ({ ...p, [k]: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                  className="h-8 text-sm bg-white"
                  placeholder="e.g. 255"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Per-Source Table */}
      <Card className="bg-white shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-heading">Per Source Breakdown</CardTitle>
            <div className="flex gap-2">
              <Select value={addSource} onValueChange={setAddSource}>
                <SelectTrigger className="h-7 text-xs w-40 bg-white">
                  <SelectValue placeholder="Add source..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {availableSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="h-7 gap-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAddSource} disabled={!addSource}>
                <Plus size={12} /> Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 pb-4">
          <div className="px-4">
            <p className="text-xs text-slate-400 mb-3">Click any cell to edit. Arches/Patients are manually entered in Sandbox mode.</p>
            <MarketingTable
              rows={[...rows, ...(tot ? [tot] : [])]}
              totals={tot}
              onRowChange={handleRowChange}
              onRemoveRow={handleRemove}
              showSaleData={false}
              roasMode={roasMode || 'roas'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary KPI Cards */}
      {tot && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiSummaryCard label="Total Spend" value={fmt$(tot.spend)} color="#3B82F6" icon={DollarSign} />
          <KpiSummaryCard label="Cost Per Lead" value={fmt$(tot.cpl)} color="#06B6D4" icon={Users}
            sub={`${fmtNum(tot.leads)} leads`} />
          <KpiSummaryCard label="Booking Rate" value={fmtPct(tot.booking_rate)} color="#10B981" icon={Phone}
            sub={`${fmtNum(tot.booked)} booked`} />
          <KpiSummaryCard label="Show Rate" value={fmtPct(tot.show_rate)} color="#8B5CF6" icon={Eye}
            sub={`${fmtNum(tot.shows)} shows`} />
          <KpiSummaryCard label="Cost Per Arch" value={fmt$(tot.cost_per_arch)} color="#F59E0B" icon={Target}
            sub={`${fmtNum(tot.arches_sold)} arches`} />
        </div>
      )}
    </div>
  );
};

// ─── Monthly Tracker ──────────────────────────────────────────────────────────
// ─── Monthly Tracker (saved entries, history, trends) ────────────────────────
const MonthlyTracker = ({ dropdowns, roasMode }) => {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  );
  const [entryData, setEntryData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [sources, setSources] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ spend: null, leads: null, booked: null, shows: null });
  const [notes, setNotes] = useState('');
  const [addSource, setAddSource] = useState('');
  const [activeTab2, setActiveTab2] = useState('entry');

  const allSources = dropdowns?.referral_sources || [];
  const usedSources = sources.map(s => s.source_name);
  const availableSources = allSources.filter(s => !usedSources.includes(s));

  // Months for selector (past 24 months)
  const monthOptions = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    monthOptions.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const [selectedLocation, setSelectedLocation] = React.useState('overall');
  const [locations, setLocations] = React.useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const locParam = selectedLocation === 'overall' ? {} : { location_id: selectedLocation };
      const [entriesRes, trendsRes, locsRes] = await Promise.all([
        axios.get(`${API}/marketing/entries`, { params: locParam }),
        axios.get(`${API}/marketing/trends`, { params: locParam }),
        axios.get(`${API}/locations`),
      ]);
      setAllEntries(entriesRes.data);
      setTrends(trendsRes.data);
      setLocations(locsRes.data.filter(l => l.active));
    } catch (e) { console.warn('[Marketing] fetchAll:', e?.message); }
  }, [selectedLocation]);

  const fetchMonth = useCallback(async (month) => {
    setLoading(true);
    try {
      const locParam = selectedLocation === 'overall' ? {} : { location_id: selectedLocation };
      const res = await axios.get(`${API}/marketing/kpis/${month}`, { params: locParam });
      setKpis(res.data);
      if (res.data.entry) {
        const e = res.data.entry;
        setSources(e.sources || []);
        setGrandTotals({
          spend: e.total_spend || null, leads: e.total_leads || null,
          booked: e.total_booked || null, shows: e.total_shows || null
        });
        setNotes(e.notes || '');
        setEntryData(e);
        setEditMode(false);
      } else {
        setSources([]);
        setGrandTotals({ spend: null, leads: null, booked: null, shows: null });
        setNotes('');
        setEntryData(null);
        setEditMode(true);
      }
    } catch {}
    setLoading(false);
  }, [selectedLocation]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchMonth(selectedMonth); }, [selectedMonth, fetchMonth]);

  const handleRowChange = (idx, key, val) => {
    setSources(prev => { const n = [...prev]; n[idx] = { ...n[idx], [key]: val }; return n; });
  };
  const handleRemove = (idx) => setSources(prev => prev.filter((_, i) => i !== idx));
  const handleAddSource = () => {
    if (!addSource) return;
    setSources(prev => [...prev, { source_name: addSource, spend: null, leads: null, booked: null, shows: null, arches_sold: null, patients_sold: null }]);
    setAddSource('');
  };

  // Compute live rows from current state while in edit mode
  const computeRows = () => {
    const rows = [];
    let s_spend = 0, s_leads = 0, s_booked = 0, s_shows = 0;
    const saleData = kpis?.sale_data || {};
    sources.forEach(s => {
      const spend = s.spend || 0, leads = s.leads || 0, booked = s.booked || 0, shows = s.shows || 0;
      s_spend += spend; s_leads += leads; s_booked += booked; s_shows += shows;
      const sd = saleData[s.source_name] || {};
      const arches = sd.arches || s.arches_sold || 0;
      const patients = sd.patients || s.patients_sold || 0;
      rows.push({
        ...s,
        arches_sold: arches, patients_sold: patients, revenue: sd.revenue || 0,
        cpl: leads > 0 ? +(spend / leads).toFixed(2) : null,
        booking_rate: leads > 0 ? +(booked / leads * 100).toFixed(1) : null,
        show_rate: booked > 0 ? +(shows / booked * 100).toFixed(1) : null,
        cost_per_show: shows > 0 ? +(spend / shows).toFixed(2) : null,
        cost_per_arch: arches > 0 ? +(spend / arches).toFixed(2) : null,
        cost_per_patient: patients > 0 ? +(spend / patients).toFixed(2) : null,
        roi: sd.revenue > 0 && spend > 0 ? +((sd.revenue - spend) / spend * 100).toFixed(1) : null,
      });
    });
    // Other row
    const o_spend = Math.max(0, (grandTotals.spend || 0) - s_spend);
    const o_leads = Math.max(0, (grandTotals.leads || 0) - s_leads);
    const o_booked = Math.max(0, (grandTotals.booked || 0) - s_booked);
    const o_shows = Math.max(0, (grandTotals.shows || 0) - s_shows);
    if (grandTotals.leads > 0 || grandTotals.spend > 0) {
      rows.push({
        source_name: 'Other (calculated)', spend: o_spend, leads: o_leads, booked: o_booked, shows: o_shows,
        arches_sold: 0, patients_sold: 0, revenue: 0,
        cpl: o_leads > 0 ? +(o_spend / o_leads).toFixed(2) : null,
        booking_rate: o_leads > 0 ? +(o_booked / o_leads * 100).toFixed(1) : null,
        show_rate: o_booked > 0 ? +(o_shows / o_booked * 100).toFixed(1) : null,
        cost_per_show: o_shows > 0 ? +(o_spend / o_shows).toFixed(2) : null,
        cost_per_arch: null, cost_per_patient: null, roi: null,
      });
    }
    // Totals
    const t_spend = grandTotals.spend || s_spend;
    const t_leads = grandTotals.leads || s_leads;
    const t_booked = grandTotals.booked || s_booked;
    const t_shows = grandTotals.shows || s_shows;
    const t_arches = rows.reduce((a, r) => a + (r.arches_sold || 0), 0);
    const t_patients = rows.reduce((a, r) => a + (r.patients_sold || 0), 0);
    const t_rev = rows.reduce((a, r) => a + (r.revenue || 0), 0);
    rows.push({
      source_name: 'TOTAL', spend: t_spend, leads: t_leads, booked: t_booked, shows: t_shows,
      arches_sold: t_arches, patients_sold: t_patients, revenue: t_rev,
      cpl: t_leads > 0 ? +(t_spend / t_leads).toFixed(2) : null,
      booking_rate: t_leads > 0 ? +(t_booked / t_leads * 100).toFixed(1) : null,
      show_rate: t_booked > 0 ? +(t_shows / t_booked * 100).toFixed(1) : null,
      cost_per_show: t_shows > 0 ? +(t_spend / t_shows).toFixed(2) : null,
      cost_per_arch: t_arches > 0 ? +(t_spend / t_arches).toFixed(2) : null,
      cost_per_patient: t_patients > 0 ? +(t_spend / t_patients).toFixed(2) : null,
      roi: t_rev > 0 && t_spend > 0 ? +((t_rev - t_spend) / t_spend * 100).toFixed(1) : null,
    });
    return rows;
  };

  const liveRows = editMode ? computeRows() : (kpis ? [...(kpis.rows || []), ...(kpis.totals ? [kpis.totals] : [])] : []);
  const liveTotals = liveRows.find(r => r.source_name === 'TOTAL');

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        month: selectedMonth,
        location_id: selectedLocation === 'overall' ? null : selectedLocation,
        sources: sources.map(s => ({ ...s })),
        total_spend: grandTotals.spend || null,
        total_leads: grandTotals.leads || null,
        total_booked: grandTotals.booked || null,
        total_shows: grandTotals.shows || null,
        notes
      };
      if (entryData) {
        await axios.put(`${API}/marketing/entries/${entryData.id}`, payload);
        toast.success('Entry updated!');
      } else {
        await axios.post(`${API}/marketing/entries`, payload);
        toast.success('Entry saved!');
      }
      await fetchMonth(selectedMonth);
      await fetchAll();
      setEditMode(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!entryData || !window.confirm('Delete this monthly entry?')) return;
    try {
      await axios.delete(`${API}/marketing/entries/${entryData.id}`);
      toast.success('Deleted');
      fetchMonth(selectedMonth);
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const formatMonthLabel = (m) => {
    try {
      const [y, mo] = m.split('-');
      return format(new Date(parseInt(y), parseInt(mo) - 1, 1), 'MMMM yyyy');
    } catch { return m; }
  };

  return (
    <div className="space-y-6">
      {/* Month selector + Location filter + actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-cyan-400" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="h-9 text-sm w-44 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {monthOptions.map(m => <SelectItem key={m} value={m}>{formatMonthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Location filter — only show when multiple locations */}
        {locations.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Location:</span>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="h-9 text-sm w-44 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="overall">Overall (All Locations)</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Badge className={`text-xs px-2 ${
          entryData ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
        }`}>
          {entryData ? '✓ Entry saved' : 'No entry yet'}
        </Badge>
        {!editMode && entryData && (
          <Button size="sm" variant="outline" className="h-8 gap-1.5 bg-white" onClick={() => setEditMode(true)}>
            <Pencil size={12} /> Edit
          </Button>
        )}
        {!editMode && entryData && (
          <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:text-red-600" onClick={handleDelete}>
            <Trash2 size={12} />
          </Button>
        )}
        {editMode && (
          <>
            <Button size="sm" className="h-8 gap-1.5 font-semibold"
              style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}
              onClick={handleSave} disabled={saving}>
              <Save size={12} /> {saving ? 'Saving...' : (entryData ? 'Update' : 'Save Entry')}
            </Button>
            {entryData && (
              <Button size="sm" variant="ghost" className="h-8" onClick={() => { fetchMonth(selectedMonth); setEditMode(false); }}>Cancel</Button>
            )}
          </>
        )}
      </div>

      <Tabs value={activeTab2} onValueChange={setActiveTab2}>
        <TabsList className="bg-white/10">
          <TabsTrigger value="entry" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300">Entry</TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300">History Table</TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300">Trend Charts</TabsTrigger>
        </TabsList>

        {/* ── Entry Tab ── */}
        <TabsContent value="entry" className="space-y-6 mt-4">
          {/* Grand Totals */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                Grand Totals
                <span className="text-xs font-normal text-slate-400">(optional — enables "Other" auto-calculation)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[['spend','Total Spend ($)'],['leads','Total Leads'],['booked','Total Booked'],['shows','Total Shows']].map(([k,lbl]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs text-slate-600">{lbl}</Label>
                    {editMode ? (
                      <Input type="number" min="0" value={safe(grandTotals[k])}
                        onChange={e => setGrandTotals(p => ({ ...p, [k]: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                        className="h-8 text-sm bg-white" placeholder="e.g. 255" />
                    ) : (
                      <p className="text-sm font-medium text-slate-800 py-1.5">
                        {grandTotals[k] != null ? (k === 'spend' ? fmt$(grandTotals[k]) : grandTotals[k].toLocaleString()) : <span className="text-slate-300">—</span>}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Source Table */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading">
                  {formatMonthLabel(selectedMonth)} — Per Source
                </CardTitle>
                {editMode && (
                  <div className="flex gap-2">
                    <Select value={addSource} onValueChange={setAddSource}>
                      <SelectTrigger className="h-7 text-xs w-40 bg-white">
                        <SelectValue placeholder="Add source..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {availableSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-7 gap-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAddSource} disabled={!addSource}>
                      <Plus size={12} /> Add
                    </Button>
                  </div>
                )}
              </div>
              {editMode && <p className="text-xs text-slate-400 mt-1">Arches & Patients auto-pulled from your sale records for {formatMonthLabel(selectedMonth)}. Click any cell to edit.</p>}
              {!editMode && <p className="text-xs text-slate-400 mt-1">Arches & Revenue auto-pulled from sale records. Click Edit to modify inputs.</p>}
            </CardHeader>
            <CardContent className="p-0 pb-4">
              <div className="px-4">
                {liveRows.length <= 1 && !editMode && (
                  <div className="text-center py-8">
                    <BarChart2 className="mx-auto mb-2 text-slate-300" size={32} />
                    <p className="text-slate-500 text-sm">No entry for {formatMonthLabel(selectedMonth)}</p>
                    <Button size="sm" className="mt-3" style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}
                      onClick={() => setEditMode(true)}>
                      <Plus size={12} className="mr-1" /> Create Entry
                    </Button>
                  </div>
                )}
                {liveRows.length > 1 && (
                  <MarketingTable
                    rows={liveRows}
                    totals={liveTotals}
                    onRowChange={editMode ? handleRowChange : undefined}
                    onRemoveRow={editMode ? handleRemove : undefined}
                    readOnly={!editMode}
                    showSaleData={true}
                    roasMode={roasMode || 'roas'}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Summary KPIs */}
          {liveTotals && liveTotals.spend > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiSummaryCard label="Total Spend" value={fmt$(liveTotals.spend)} color="#3B82F6" icon={DollarSign} />
              <KpiSummaryCard label="Cost Per Lead" value={fmt$(liveTotals.cpl)} color="#06B6D4" icon={Users}
                sub={`${fmtNum(liveTotals.leads)} leads`} />
              <KpiSummaryCard label="Booking Rate" value={fmtPct(liveTotals.booking_rate)} color="#10B981" icon={Phone}
                sub={`${fmtNum(liveTotals.booked)} booked`} />
              <KpiSummaryCard label="Show Rate" value={fmtPct(liveTotals.show_rate)} color="#8B5CF6" icon={Eye}
                sub={`${fmtNum(liveTotals.shows)} shows`} />
              <KpiSummaryCard label="Cost Per Arch" value={fmt$(liveTotals.cost_per_arch)} color="#F59E0B" icon={Target}
                sub={`${fmtNum(liveTotals.arches_sold)} arches`} />
            </div>
          )}

          {/* Notes */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Notes</CardTitle></CardHeader>
            <CardContent>
              {editMode ? (
                <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Campaign notes, observations, context..."
                  className="bg-white border-slate-200 min-h-[60px] text-sm" />
              ) : (
                <p className="text-sm text-slate-600">{notes || <span className="text-slate-300">No notes</span>}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── History Table Tab ── */}
        <TabsContent value="history" className="mt-4">
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading">All Monthly Entries</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(!trends?.trend_data || trends.trend_data.length === 0) ? (
                <div className="text-center py-12">
                  <CalendarDays className="mx-auto mb-2 text-slate-300" size={32} />
                  <p className="text-slate-500 text-sm">No entries yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {['Month','Spend','Leads','Booked','Shows','CPL','Book%','Show%','$/Show','Arches','Patients','$/Arch','$/Patient'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                        ))}
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...trends.trend_data].reverse().map((row, i) => (
                        <tr key={row.month} className={`border-b border-slate-50 hover:bg-slate-50 ${ row.month === selectedMonth ? 'bg-cyan-50' : '' }`}>
                          <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">
                            <button className="hover:text-cyan-600" onClick={() => { setSelectedMonth(row.month); setActiveTab2('entry'); }}>
                              {formatMonthLabel(row.month)}
                            </button>
                          </td>
                          <td className="px-3 py-2"><CalcCell value={row.total_spend} fmt={fmt$} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.total_leads} fmt={fmtNum} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.total_booked} fmt={fmtNum} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.total_shows} fmt={fmtNum} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.cpl} fmt={fmt$} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.booking_rate} fmt={fmtPct} good={50} bad={25} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.show_rate} fmt={fmtPct} good={60} bad={35} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.total_shows > 0 ? row.total_spend / row.total_shows : null} fmt={fmt$} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.arches_sold} fmt={fmtNum} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.patients_sold} fmt={fmtNum} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.cost_per_arch} fmt={fmt$} /></td>
                          <td className="px-3 py-2"><CalcCell value={row.cost_per_patient} fmt={fmt$} /></td>
                          <td className="px-3 py-2">
                            <button className="text-cyan-500 hover:text-cyan-700 text-xs" onClick={() => { setSelectedMonth(row.month); setActiveTab2('entry'); }}>
                              View →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Trends Tab ── */}
        <TabsContent value="trends" className="mt-4 space-y-6">
          {(!trends?.trend_data || trends.trend_data.length < 2) ? (
            <Card className="bg-white shadow-sm">
              <CardContent className="py-16 text-center">
                <TrendingUp className="mx-auto mb-2 text-slate-300" size={40} />
                <p className="text-slate-500">Need at least 2 months of data to show trends</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* CPL Trend */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Cost Per Lead — Monthly Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trends.trend_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => '$' + v} />
                      <RechartTooltip formatter={(v) => ['$' + Number(v).toLocaleString(), 'CPL']} />
                      <Line type="monotone" dataKey="cpl" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 4 }} name="CPL" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              {/* Cost per Arch Trend */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Cost Per Arch — Monthly Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trends.trend_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => '$' + (v/1000).toFixed(0) + 'k'} />
                      <RechartTooltip formatter={(v) => ['$' + Number(v).toLocaleString(), '$/Arch']} />
                      <Line type="monotone" dataKey="cost_per_arch" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 4 }} name="$/Arch" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              {/* Booking & Show Rate Trend */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Booking Rate & Show Rate — Monthly Trend</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trends.trend_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                      <RechartTooltip formatter={(v) => [Number(v).toFixed(1) + '%']} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="booking_rate" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} name="Booking %" />
                      <Line type="monotone" dataKey="show_rate" stroke="#8B5CF6" strokeWidth={2.5} dot={{ r: 4 }} name="Show %" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              {/* Spend vs Revenue */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Monthly Spend vs Arches Sold</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={trends.trend_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => '$' + (v/1000).toFixed(0) + 'k'} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <RechartTooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left" dataKey="total_spend" fill="#3B82F6" name="Spend ($)" radius={[2,2,0,0]} />
                      <Bar yAxisId="right" dataKey="arches_sold" fill="#06B6D4" name="Arches Sold" radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ─── Channel Comparison Tab ───────────────────────────────────────────────────
// ─── Channel Comparison Over Time ─────────────────────────────────────────────
const ChannelComparison = ({ allEntries }) => {
  const [selectedSource, setSelectedSource] = useState('');
  const allSources = React.useMemo(() => {
    const set = new Set();
    allEntries.forEach(e => (e.sources || []).forEach(s => set.add(s.source_name)));
    return [...set].sort();
  }, [allEntries]);

  const data = React.useMemo(() => {
    if (!selectedSource) return [];
    return allEntries.map(entry => {
      const src = (entry.sources || []).find(s => s.source_name === selectedSource) || {};
      const spend = src.spend || 0;
      const mgmt = src.management_fees || 0;
      const total_cost = spend + mgmt;
      const leads = src.leads || 0;
      const booked = src.booked || 0;
      const shows = src.shows || 0;
      return {
        month: entry.month,
        spend, management_fees: mgmt, total_cost, leads, booked, shows,
        cpl: leads > 0 ? Math.round(spend / leads) : null,
        cost_per_booking: booked > 0 ? Math.round(spend / booked) : null,
        booking_rate: leads > 0 ? +(booked / leads * 100).toFixed(1) : null,
        show_rate: booked > 0 ? +(shows / booked * 100).toFixed(1) : null,
      };
    }).filter(d => d.spend > 0 || d.leads > 0);
  }, [selectedSource, allEntries]);

  if (allEntries.length === 0) return (
    <div className="text-center py-16 text-slate-400"><CalendarDays size={40} className="mx-auto mb-2 text-slate-600" /><p>No monthly entries yet</p></div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-300 font-medium">Compare channel over time:</label>
        <Select value={selectedSource} onValueChange={setSelectedSource}>
          <SelectTrigger className="w-48 h-8 text-xs bg-white"><SelectValue placeholder="Select source..." /></SelectTrigger>
          <SelectContent className="bg-white">
            {allSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!selectedSource ? (
        <div className="text-center py-10 text-slate-500 text-sm">Select a marketing source to compare its performance month over month</div>
      ) : data.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">No data found for {selectedSource}</div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">{selectedSource} — CPL & $/Booking over time</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => '$' + v} />
                    <RechartTooltip formatter={(v) => ['$' + Number(v).toLocaleString()]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="cpl" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 4 }} name="CPL ($)" />
                    <Line type="monotone" dataKey="cost_per_booking" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 4 }} name="$/Booking" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">{selectedSource} — Booking % & Show % over time</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} unit="%" />
                    <RechartTooltip formatter={(v) => [Number(v).toFixed(1) + '%']} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="booking_rate" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} name="Booking %" />
                    <Line type="monotone" dataKey="show_rate" stroke="#8B5CF6" strokeWidth={2.5} dot={{ r: 4 }} name="Show %" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          {/* Data table */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">{selectedSource} — Monthly Data Table</CardTitle></CardHeader>
            <CardContent className="p-0 pb-4">
              <div className="overflow-x-auto px-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      {['Month','Spend','Mgmt Fees','Leads','Booked','Shows','CPL','$/Booking','Book%','Show%'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.month} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{row.month}</td>
                        <td className="px-3 py-2"><CalcCell value={row.spend} fmt={fmt$} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.management_fees} fmt={fmt$} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.leads} fmt={fmtNum} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.booked} fmt={fmtNum} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.shows} fmt={fmtNum} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.cpl} fmt={fmt$} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.cost_per_booking} fmt={fmt$} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.booking_rate} fmt={fmtPct} good={50} bad={25} /></td>
                        <td className="px-3 py-2"><CalcCell value={row.show_rate} fmt={fmtPct} good={60} bad={35} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
// ─── Main Page ──────────────────────────────────────────────────────────────────
export default function MarketingCalculatorPage() {
  const [dropdowns, setDropdowns] = useState({});
  const [tab, setTab] = useState('monthly');
  const [roasMode, setRoasMode] = useState('roas'); // 'roas' | 'overall'
  const [allEntries, setAllEntries] = useState([]);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    axios.get(`${API}/dropdowns`).then(r => setDropdowns(r.data)).catch(e => console.warn("[Marketing]", e?.message || e));
    axios.get(`${API}/marketing/entries`).then(r => setAllEntries(r.data)).catch(e => console.warn("[Marketing]", e?.message || e));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Calculator size={22} className="text-cyan-400" />
            Marketing ROI Calculator
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track spend, CPL, booking rates, and cost per arch — by source, by month.
          </p>
        </div>
        {/* ROAS / Overall ROI Toggle */}
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          <span className="text-xs text-slate-400 font-medium">Metric view:</span>
          <button onClick={() => setRoasMode('roas')}
            className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-colors ${
              roasMode === 'roas' ? 'bg-cyan-500 text-slate-900' : 'text-slate-400 hover:text-white'
            }`}>
            ROAS (ads only)
          </button>
          <button onClick={() => setRoasMode('overall')}
            className={`text-xs px-2.5 py-1 rounded-md font-semibold transition-colors ${
              roasMode === 'overall' ? 'bg-cyan-500 text-slate-900' : 'text-slate-400 hover:text-white'
            }`}>
            Overall ROI (ads + mgmt)
          </button>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/10">
          <TabsTrigger value="monthly" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300 gap-1.5">
            <CalendarDays size={14} /> Monthly Tracker
          </TabsTrigger>
          <TabsTrigger value="channel" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300 gap-1.5">
            <BarChart2 size={14} /> Channel Over Time
          </TabsTrigger>
          <TabsTrigger value="sandbox" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-300 gap-1.5">
            <Calculator size={14} /> Sandbox
          </TabsTrigger>
        </TabsList>
        <TabsContent value="monthly" className="mt-4">
          <MonthlyTracker dropdowns={dropdowns} roasMode={roasMode} />
        </TabsContent>
        <TabsContent value="channel" className="mt-4">
          <ChannelComparison allEntries={allEntries} />
        </TabsContent>
        <TabsContent value="sandbox" className="mt-4">
          <SandboxCalculator dropdowns={dropdowns} roasMode={roasMode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
